import type { LangMap } from "./index";

const en = {
  title: "Explore",
  subtitle: "Every Lumen tool, in one place",
  search: "Search a section…",
  empty: "Nothing matches your search.",
  open: "Open",
  groups: {
    study: "Study & understand",
    create: "Create materials",
    review: "Review & test",
    plan: "Plan & progress",
    social: "Community",
    account: "Account",
    teach: "Teaching",
    family: "Family",
  },
  items: {
    ai: { title: "AI Tutor", sub: "Ask anything" },
    learn: { title: "Methods", sub: "How to study better" },
    studio: { title: "Study session", sub: "Timer and focus" },
    programs: { title: "Programs", sub: "Syllabus and study path" },
    prep: { title: "Get me ready", sub: "Preview tomorrow's lessons" },
    tools: { title: "Podcasts & maps", sub: "Listen or visualise" },
    flashcards: { title: "Flashcards", sub: "Spaced review" },
    quiz: { title: "AI quiz", sub: "Test yourself" },
    breaks: { title: "Breaks & exams", sub: "Recovery and exam plans" },
    planner: { title: "Planner", sub: "Calendar and deadlines" },
    stats: { title: "Statistics", sub: "Your progress" },
    rewards: { title: "Rewards", sub: "XP, levels and badges" },
    widget: { title: "Home widget", sub: "Choose what to show" },
    community: { title: "Community", sub: "Friends, challenges, notes" },
    profile: { title: "Profile", sub: "Settings and accessibility" },
    profiles: { title: "Profiles", sub: "Switch or add a profile" },
    family: { title: "Family", sub: "Follow your children" },
    teacher: { title: "Classes", sub: "Your virtual classes" },
    teachingStudio: { title: "Content studio", sub: "Create lessons and tests" },
    teacherResults: { title: "Results", sub: "Class analytics" },
    teacherHub: { title: "Teachers hub", sub: "Share with colleagues" },
  },
};

const today = {
  en: {
    title: "Today",
    subtitle: "Start here: {{count}} things that matter now",
    exploreCta: "Explore all sections",
    more: "More for today",
    moreSub: "Class notices, homework and widgets",
    actions: {
      exam: {
        title: "Prepare {{subject}}",
        sub: "Test in {{days}} day(s): build your plan",
        cta: "Prepare",
      },
      homework: { title: "Homework: {{subject}}", sub: "Due in {{days}} day(s)", cta: "Open" },
      review: { title: "Review {{count}} cards", sub: "They are due today", cta: "Review" },
      session: { title: "Study {{min}} min", sub: "Reach today's goal", cta: "Start" },
      method: { title: "Learn a new method", sub: "5 minutes to study smarter", cta: "Discover" },
    },
    resume: {
      title: "Pick up where you left off",
      subtitle: "Chosen from your latest attempts and weak spots",
      cta: "Resume",
      kinds: {
        quiz: { title: "Retry: {{title}}", sub: "Last attempt: {{pct}}% correct" },
        difficulty: { title: "Work on {{topic}}", sub: "{{subject}}: {{count}} mistakes logged" },
        cards: { title: "Review {{title}}", sub: "{{count}} cards due in this deck" },
        chat: { title: "Continue: {{title}}", sub: "Your last chat with Lumen" },
        note: { title: "Reopen: {{title}}", sub: "Your last notes" },
        map: { title: "Reopen map: {{title}}", sub: "Where you left the map" },
        session: { title: "Back to {{title}}", sub: "Your last study session" },
      },
      focus: {
        label: "Focus mode",
        duration: "Session length",
        start: "Start focus",
        goal: "Stay on this for {{min}} min",
        stop: "End focus session",
        aria: "Focus session in progress",
        done: "Focus done: {{min}} min. Great job!",
        stopped: "Focus stopped after {{min}} min",
      },
    },
  },
};

const it = {
  title: "Esplora",
  subtitle: "Tutti gli strumenti di Lumen, in un posto solo",
  search: "Cerca una sezione…",
  empty: "Nessun risultato per la tua ricerca.",
  open: "Apri",
  groups: {
    study: "Studia e comprendi",
    create: "Crea materiali",
    review: "Ripassa e mettiti alla prova",
    plan: "Pianifica e progressi",
    social: "Community",
    account: "Account",
    teach: "Insegnamento",
    family: "Famiglia",
  },
  items: {
    ai: { title: "Tutor AI", sub: "Chiedi qualsiasi cosa" },
    learn: { title: "Metodi", sub: "Come studiare meglio" },
    studio: { title: "Sessione di studio", sub: "Timer e concentrazione" },
    programs: { title: "Programmi", sub: "Programma e percorso" },
    prep: { title: "Preparami", sub: "Anteprima delle lezioni" },
    tools: { title: "Podcast & mappe", sub: "Ascolta o visualizza" },
    flashcards: { title: "Flashcard", sub: "Ripasso spaziato" },
    quiz: { title: "Quiz AI", sub: "Mettiti alla prova" },
    breaks: { title: "Vacanze & esami", sub: "Recupero e preparazione" },
    planner: { title: "Planner", sub: "Calendario e scadenze" },
    stats: { title: "Statistiche", sub: "I tuoi progressi" },
    rewards: { title: "Premi", sub: "XP, livelli e badge" },
    widget: { title: "Widget home", sub: "Scegli cosa mostrare" },
    community: { title: "Community", sub: "Amici, sfide, appunti" },
    profile: { title: "Profilo", sub: "Impostazioni e accessibilità" },
    profiles: { title: "Profili", sub: "Cambia o aggiungi profilo" },
    family: { title: "Famiglia", sub: "Segui i tuoi figli" },
    teacher: { title: "Classi", sub: "Le tue classi virtuali" },
    teachingStudio: { title: "Studio contenuti", sub: "Crea lezioni e verifiche" },
    teacherResults: { title: "Risultati", sub: "Andamento della classe" },
    teacherHub: { title: "Hub docenti", sub: "Condividi con i colleghi" },
  },
};

const todayIt = {
  title: "Oggi",
  subtitle: "Parti da qui: {{count}} cose che contano adesso",
  exploreCta: "Esplora tutte le sezioni",
  more: "Altro per oggi",
  moreSub: "Avvisi della classe, compiti e widget",
  actions: {
    exam: {
      title: "Prepara {{subject}}",
      sub: "Verifica tra {{days}} giorni: crea il piano",
      cta: "Prepara",
    },
    homework: {
      title: "Compiti: {{subject}}",
      sub: "Da consegnare tra {{days}} giorni",
      cta: "Apri",
    },
    review: { title: "Ripassa {{count}} carte", sub: "Sono in scadenza oggi", cta: "Ripassa" },
    session: { title: "Studia {{min}} min", sub: "Raggiungi l'obiettivo di oggi", cta: "Inizia" },
    method: { title: "Impara un metodo nuovo", sub: "5 minuti per studiare meglio", cta: "Scopri" },
  },
  resume: {
    title: "Riprendi da dove hai lasciato",
    subtitle: "Scelto dai tuoi ultimi tentativi e dalle tue difficoltà",
    cta: "Riprendi",
    kinds: {
      quiz: { title: "Riprova: {{title}}", sub: "Ultimo tentativo: {{pct}}% corretto" },
      difficulty: { title: "Lavora su {{topic}}", sub: "{{subject}}: {{count}} errori registrati" },
      cards: { title: "Ripassa {{title}}", sub: "{{count}} carte in scadenza in questo mazzo" },
      chat: { title: "Continua: {{title}}", sub: "L'ultima chat con Lumen" },
      note: { title: "Riapri: {{title}}", sub: "I tuoi ultimi appunti" },
      map: { title: "Riapri la mappa: {{title}}", sub: "Dove avevi lasciato la mappa" },
      session: { title: "Torna a {{title}}", sub: "La tua ultima sessione di studio" },
    },
    focus: {
      label: "Modalità Focus",
      duration: "Durata della sessione",
      start: "Avvia focus",
      goal: "Resta su questo per {{min}} min",
      stop: "Termina la sessione focus",
      aria: "Sessione focus in corso",
      done: "Focus completato: {{min}} min. Bravissimo!",
      stopped: "Focus interrotto dopo {{min}} min",
    },
  },
};

const es = {
  title: "Explorar",
  subtitle: "Todas las herramientas de Lumen en un solo lugar",
  search: "Busca una sección…",
  empty: "Sin resultados.",
  open: "Abrir",
  groups: {
    study: "Estudia y comprende",
    create: "Crea materiales",
    review: "Repasa y ponte a prueba",
    plan: "Planifica y progresa",
    social: "Comunidad",
    account: "Cuenta",
    teach: "Enseñanza",
    family: "Familia",
  },
  items: {
    ai: { title: "Tutor IA", sub: "Pregunta lo que quieras" },
    learn: { title: "Métodos", sub: "Cómo estudiar mejor" },
    studio: { title: "Sesión de estudio", sub: "Temporizador y concentración" },
    programs: { title: "Programas", sub: "Temario y ruta de estudio" },
    prep: { title: "Prepárame", sub: "Vista previa de las clases" },
    tools: { title: "Pódcasts y mapas", sub: "Escucha o visualiza" },
    flashcards: { title: "Tarjetas", sub: "Repaso espaciado" },
    quiz: { title: "Cuestionario IA", sub: "Ponte a prueba" },
    breaks: { title: "Vacaciones y exámenes", sub: "Recuperación y preparación" },
    planner: { title: "Agenda", sub: "Calendario y entregas" },
    stats: { title: "Estadísticas", sub: "Tu progreso" },
    rewards: { title: "Recompensas", sub: "XP, niveles e insignias" },
    widget: { title: "Widget de inicio", sub: "Elige qué mostrar" },
    community: { title: "Comunidad", sub: "Amigos, retos, apuntes" },
    profile: { title: "Perfil", sub: "Ajustes y accesibilidad" },
    profiles: { title: "Perfiles", sub: "Cambia o añade un perfil" },
    family: { title: "Familia", sub: "Sigue a tus hijos" },
    teacher: { title: "Clases", sub: "Tus clases virtuales" },
    teachingStudio: { title: "Estudio de contenidos", sub: "Crea clases y pruebas" },
    teacherResults: { title: "Resultados", sub: "Analítica de la clase" },
    teacherHub: { title: "Hub de docentes", sub: "Comparte con colegas" },
  },
};

const fr = {
  title: "Explorer",
  subtitle: "Tous les outils Lumen au même endroit",
  search: "Rechercher une section…",
  empty: "Aucun résultat.",
  open: "Ouvrir",
  groups: {
    study: "Étudier et comprendre",
    create: "Créer des supports",
    review: "Réviser et s'entraîner",
    plan: "Planifier et progresser",
    social: "Communauté",
    account: "Compte",
    teach: "Enseignement",
    family: "Famille",
  },
  items: {
    ai: { title: "Tuteur IA", sub: "Pose n'importe quelle question" },
    learn: { title: "Méthodes", sub: "Mieux apprendre" },
    studio: { title: "Séance de révision", sub: "Minuteur et concentration" },
    programs: { title: "Programmes", sub: "Programme et parcours" },
    prep: { title: "Prépare-moi", sub: "Aperçu des prochains cours" },
    tools: { title: "Podcasts et cartes", sub: "Écoute ou visualise" },
    flashcards: { title: "Cartes mémo", sub: "Révision espacée" },
    quiz: { title: "Quiz IA", sub: "Teste-toi" },
    breaks: { title: "Vacances et examens", sub: "Rattrapage et préparation" },
    planner: { title: "Agenda", sub: "Calendrier et échéances" },
    stats: { title: "Statistiques", sub: "Tes progrès" },
    rewards: { title: "Récompenses", sub: "XP, niveaux et badges" },
    widget: { title: "Widget d'accueil", sub: "Choisis ce qui s'affiche" },
    community: { title: "Communauté", sub: "Amis, défis, notes" },
    profile: { title: "Profil", sub: "Réglages et accessibilité" },
    profiles: { title: "Profils", sub: "Changer ou ajouter un profil" },
    family: { title: "Famille", sub: "Suis tes enfants" },
    teacher: { title: "Classes", sub: "Tes classes virtuelles" },
    teachingStudio: { title: "Studio de contenus", sub: "Crée cours et évaluations" },
    teacherResults: { title: "Résultats", sub: "Analyse de la classe" },
    teacherHub: { title: "Hub enseignants", sub: "Partage avec tes collègues" },
  },
};

const de = {
  title: "Entdecken",
  subtitle: "Alle Lumen-Tools an einem Ort",
  search: "Bereich suchen…",
  empty: "Keine Treffer.",
  open: "Öffnen",
  groups: {
    study: "Lernen und verstehen",
    create: "Materialien erstellen",
    review: "Wiederholen und testen",
    plan: "Planen und Fortschritt",
    social: "Community",
    account: "Konto",
    teach: "Unterricht",
    family: "Familie",
  },
  items: {
    ai: { title: "KI-Tutor", sub: "Frag alles" },
    learn: { title: "Methoden", sub: "Besser lernen" },
    studio: { title: "Lernsession", sub: "Timer und Fokus" },
    programs: { title: "Lehrpläne", sub: "Stoff und Lernpfad" },
    prep: { title: "Vorbereitung", sub: "Vorschau auf die Stunden" },
    tools: { title: "Podcasts & Mindmaps", sub: "Hören oder visualisieren" },
    flashcards: { title: "Karteikarten", sub: "Verteiltes Wiederholen" },
    quiz: { title: "KI-Quiz", sub: "Teste dich" },
    breaks: { title: "Ferien & Prüfungen", sub: "Nachholen und vorbereiten" },
    planner: { title: "Planer", sub: "Kalender und Fristen" },
    stats: { title: "Statistiken", sub: "Dein Fortschritt" },
    rewards: { title: "Belohnungen", sub: "XP, Level und Abzeichen" },
    widget: { title: "Home-Widget", sub: "Wähle die Inhalte" },
    community: { title: "Community", sub: "Freunde, Challenges, Notizen" },
    profile: { title: "Profil", sub: "Einstellungen und Barrierefreiheit" },
    profiles: { title: "Profile", sub: "Profil wechseln oder hinzufügen" },
    family: { title: "Familie", sub: "Begleite deine Kinder" },
    teacher: { title: "Klassen", sub: "Deine virtuellen Klassen" },
    teachingStudio: { title: "Content-Studio", sub: "Stunden und Tests erstellen" },
    teacherResults: { title: "Ergebnisse", sub: "Klassenauswertung" },
    teacherHub: { title: "Lehrer-Hub", sub: "Mit Kolleg:innen teilen" },
  },
};

const pt = {
  title: "Explorar",
  subtitle: "Todas as ferramentas do Lumen num só lugar",
  search: "Procurar uma secção…",
  empty: "Sem resultados.",
  open: "Abrir",
  groups: {
    study: "Estudar e compreender",
    create: "Criar materiais",
    review: "Rever e testar",
    plan: "Planear e progredir",
    social: "Comunidade",
    account: "Conta",
    teach: "Ensino",
    family: "Família",
  },
  items: {
    ai: { title: "Tutor IA", sub: "Pergunta o que quiseres" },
    learn: { title: "Métodos", sub: "Como estudar melhor" },
    studio: { title: "Sessão de estudo", sub: "Temporizador e foco" },
    programs: { title: "Programas", sub: "Programa e percurso" },
    prep: { title: "Prepara-me", sub: "Pré-visualizar as aulas" },
    tools: { title: "Podcasts e mapas", sub: "Ouve ou visualiza" },
    flashcards: { title: "Flashcards", sub: "Revisão espaçada" },
    quiz: { title: "Quiz IA", sub: "Testa-te" },
    breaks: { title: "Férias e exames", sub: "Recuperação e preparação" },
    planner: { title: "Planeador", sub: "Calendário e prazos" },
    stats: { title: "Estatísticas", sub: "O teu progresso" },
    rewards: { title: "Recompensas", sub: "XP, níveis e distintivos" },
    widget: { title: "Widget do ecrã inicial", sub: "Escolhe o que mostrar" },
    community: { title: "Comunidade", sub: "Amigos, desafios, apontamentos" },
    profile: { title: "Perfil", sub: "Definições e acessibilidade" },
    profiles: { title: "Perfis", sub: "Mudar ou adicionar perfil" },
    family: { title: "Família", sub: "Acompanha os teus filhos" },
    teacher: { title: "Turmas", sub: "As tuas turmas virtuais" },
    teachingStudio: { title: "Estúdio de conteúdos", sub: "Cria aulas e testes" },
    teacherResults: { title: "Resultados", sub: "Análise da turma" },
    teacherHub: { title: "Hub de docentes", sub: "Partilha com colegas" },
  },
};

const todayEs = {
  title: "Hoy",
  subtitle: "Empieza aquí: {{count}} cosas que importan ahora",
  exploreCta: "Explorar todas las secciones",
  more: "Más para hoy",
  moreSub: "Avisos de clase, tareas y widgets",
  actions: {
    exam: { title: "Prepara {{subject}}", sub: "Examen en {{days}} día(s): crea tu plan", cta: "Preparar" },
    homework: { title: "Tarea: {{subject}}", sub: "Para entregar en {{days}} día(s)", cta: "Abrir" },
    review: { title: "Repasa {{count}} tarjetas", sub: "Vencen hoy", cta: "Repasar" },
    session: { title: "Estudia {{min}} min", sub: "Alcanza el objetivo de hoy", cta: "Empezar" },
    method: { title: "Aprende un método nuevo", sub: "5 minutos para estudiar mejor", cta: "Descubrir" },
  },
  resume: {
    title: "Retoma donde lo dejaste",
    subtitle: "Elegido según tus últimos intentos y tus dificultades",
    cta: "Retomar",
    kinds: {
      quiz: { title: "Reintenta: {{title}}", sub: "Último intento: {{pct}}% correcto" },
      difficulty: { title: "Trabaja {{topic}}", sub: "{{subject}}: {{count}} errores registrados" },
      cards: { title: "Repasa {{title}}", sub: "{{count}} tarjetas pendientes en este mazo" },
      chat: { title: "Continúa: {{title}}", sub: "Tu última conversación con Lumen" },
      note: { title: "Reabre: {{title}}", sub: "Tus últimos apuntes" },
      map: { title: "Reabre el mapa: {{title}}", sub: "Donde dejaste el mapa" },
      session: { title: "Vuelve a {{title}}", sub: "Tu última sesión de estudio" },
    },
    focus: {
      label: "Modo Concentración",
      duration: "Duración de la sesión",
      start: "Iniciar concentración",
      goal: "Quédate en esto {{min}} min",
      stop: "Terminar la sesión",
      aria: "Sesión de concentración en curso",
      done: "¡Sesión completada: {{min}} min. Muy bien!",
      stopped: "Sesión detenida tras {{min}} min",
    },
  },
};

const todayFr = {
  title: "Aujourd'hui",
  subtitle: "Commence ici : {{count}} choses qui comptent maintenant",
  exploreCta: "Explorer toutes les sections",
  more: "Plus pour aujourd'hui",
  moreSub: "Annonces de classe, devoirs et widgets",
  actions: {
    exam: { title: "Prépare {{subject}}", sub: "Contrôle dans {{days}} jour(s) : crée ton plan", cta: "Préparer" },
    homework: { title: "Devoirs : {{subject}}", sub: "À rendre dans {{days}} jour(s)", cta: "Ouvrir" },
    review: { title: "Révise {{count}} cartes", sub: "Elles arrivent à échéance aujourd'hui", cta: "Réviser" },
    session: { title: "Révise {{min}} min", sub: "Atteins l'objectif du jour", cta: "Commencer" },
    method: { title: "Apprends une nouvelle méthode", sub: "5 minutes pour mieux réviser", cta: "Découvrir" },
  },
  resume: {
    title: "Reprends où tu t'es arrêté",
    subtitle: "Choisi d'après tes derniers essais et tes difficultés",
    cta: "Reprendre",
    kinds: {
      quiz: { title: "Réessaie : {{title}}", sub: "Dernier essai : {{pct}}% de réussite" },
      difficulty: { title: "Travaille {{topic}}", sub: "{{subject}} : {{count}} erreurs enregistrées" },
      cards: { title: "Révise {{title}}", sub: "{{count}} cartes à revoir dans ce paquet" },
      chat: { title: "Continue : {{title}}", sub: "Ta dernière discussion avec Lumen" },
      note: { title: "Rouvre : {{title}}", sub: "Tes dernières notes" },
      map: { title: "Rouvre la carte : {{title}}", sub: "Là où tu avais laissé la carte" },
      session: { title: "Retour à {{title}}", sub: "Ta dernière séance de révision" },
    },
    focus: {
      label: "Mode Focus",
      duration: "Durée de la séance",
      start: "Lancer le focus",
      goal: "Reste dessus pendant {{min}} min",
      stop: "Terminer la séance focus",
      aria: "Séance focus en cours",
      done: "Focus terminé : {{min}} min. Bravo !",
      stopped: "Focus arrêté après {{min}} min",
    },
  },
};

const todayDe = {
  title: "Heute",
  subtitle: "Starte hier: {{count}} Dinge, die jetzt zählen",
  exploreCta: "Alle Bereiche entdecken",
  more: "Mehr für heute",
  moreSub: "Klassen-Infos, Hausaufgaben und Widgets",
  actions: {
    exam: { title: "{{subject}} vorbereiten", sub: "Test in {{days}} Tag(en): Plan erstellen", cta: "Vorbereiten" },
    homework: { title: "Hausaufgabe: {{subject}}", sub: "Fällig in {{days}} Tag(en)", cta: "Öffnen" },
    review: { title: "{{count}} Karten wiederholen", sub: "Sie sind heute fällig", cta: "Wiederholen" },
    session: { title: "{{min}} Min lernen", sub: "Erreiche dein Tagesziel", cta: "Starten" },
    method: { title: "Neue Methode lernen", sub: "5 Minuten für besseres Lernen", cta: "Entdecken" },
  },
  resume: {
    title: "Mach dort weiter, wo du aufgehört hast",
    subtitle: "Ausgewählt nach deinen letzten Versuchen und Schwächen",
    cta: "Weitermachen",
    kinds: {
      quiz: { title: "Nochmal: {{title}}", sub: "Letzter Versuch: {{pct}}% richtig" },
      difficulty: { title: "Arbeite an {{topic}}", sub: "{{subject}}: {{count}} Fehler erfasst" },
      cards: { title: "{{title}} wiederholen", sub: "{{count}} fällige Karten in diesem Stapel" },
      chat: { title: "Weiter: {{title}}", sub: "Dein letzter Chat mit Lumen" },
      note: { title: "Wieder öffnen: {{title}}", sub: "Deine letzten Notizen" },
      map: { title: "Mindmap öffnen: {{title}}", sub: "Wo du die Mindmap verlassen hast" },
      session: { title: "Zurück zu {{title}}", sub: "Deine letzte Lernsession" },
    },
    focus: {
      label: "Fokus-Modus",
      duration: "Dauer der Session",
      start: "Fokus starten",
      goal: "Bleib {{min}} Min dran",
      stop: "Fokus-Session beenden",
      aria: "Fokus-Session läuft",
      done: "Fokus geschafft: {{min}} Min. Super!",
      stopped: "Fokus nach {{min}} Min beendet",
    },
  },
};

const todayPt = {
  title: "Hoje",
  subtitle: "Começa aqui: {{count}} coisas que importam agora",
  exploreCta: "Explorar todas as secções",
  more: "Mais para hoje",
  moreSub: "Avisos da turma, trabalhos e widgets",
  actions: {
    exam: { title: "Prepara {{subject}}", sub: "Teste em {{days}} dia(s): cria o teu plano", cta: "Preparar" },
    homework: { title: "Trabalho: {{subject}}", sub: "Entrega em {{days}} dia(s)", cta: "Abrir" },
    review: { title: "Revê {{count}} cartões", sub: "Estão a vencer hoje", cta: "Rever" },
    session: { title: "Estuda {{min}} min", sub: "Atinge o objetivo de hoje", cta: "Começar" },
    method: { title: "Aprende um método novo", sub: "5 minutos para estudar melhor", cta: "Descobrir" },
  },
  resume: {
    title: "Retoma onde ficaste",
    subtitle: "Escolhido pelas tuas últimas tentativas e dificuldades",
    cta: "Retomar",
    kinds: {
      quiz: { title: "Tenta de novo: {{title}}", sub: "Última tentativa: {{pct}}% correto" },
      difficulty: { title: "Trabalha {{topic}}", sub: "{{subject}}: {{count}} erros registados" },
      cards: { title: "Revê {{title}}", sub: "{{count}} cartões a vencer neste baralho" },
      chat: { title: "Continua: {{title}}", sub: "A tua última conversa com o Lumen" },
      note: { title: "Reabrir: {{title}}", sub: "Os teus últimos apontamentos" },
      map: { title: "Reabrir o mapa: {{title}}", sub: "Onde deixaste o mapa" },
      session: { title: "Voltar a {{title}}", sub: "A tua última sessão de estudo" },
    },
    focus: {
      label: "Modo Foco",
      duration: "Duração da sessão",
      start: "Iniciar foco",
      goal: "Fica nisto durante {{min}} min",
      stop: "Terminar a sessão de foco",
      aria: "Sessão de foco em curso",
      done: "Foco concluído: {{min}} min. Muito bem!",
      stopped: "Foco interrompido após {{min}} min",
    },
  },
};

export const explore: LangMap = { en, it, es, fr, de, pt };

export const todayFocus: LangMap = {
  en: today.en,
  it: todayIt,
  es: todayEs,
  fr: todayFr,
  de: todayDe,
  pt: todayPt,
};
